import { useEffect, useState } from "react";
import { SearchableSelect } from "@/components/shared/SearchableSelect";
import { useAuth } from "@/context/AuthContext";
import client, { getErrorMessage } from "@/api/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus } from "lucide-react";
import { PaginationBar } from "@/components/shared/PaginationBar";
import { usePaymentDeskRefData } from "./shared/usePaymentDeskRefData";
import PostFactoEntryFormDialog from "./PostFactoEntryFormDialog";
import { PayeeCell } from "./shared/PayeeCell";
import { DeleteEntryDialog } from "./shared/DeleteEntryDialog";

function money(n) {
  return `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}

const CASH_CREATE_ROLES = ["Director", "Manager", "Accounts", "Back Office"];
const CASH_REIMBURSE_ROLES = ["Manager", "Accounts"];
const UPI_CREATE_ROLES = ["Director", "Manager", "Accounts", "Back Office"];
const STATUS_BADGE = { Logged: "bg-amber-500", Reimbursed: "bg-emerald-600" };

// Cash & UPI post-facto logging (Phase 2) — payment mode is implied by
// entry type, no mode picker on either form. Shared list/dialog shell
// parametrized by `mode`; per-entry can_edit/can_delete/can_reimburse
// flags come straight from the backend (mirrors the RTGS pipeline).
export default function PostFactoEntriesTab({ mode }) {
  const { user } = useAuth();
  const createRoles = mode === "Cash" ? CASH_CREATE_ROLES : UPI_CREATE_ROLES;
  const canCreate = createRoles.includes(user?.role);
  const canReimburseRole = mode === "Cash" && CASH_REIMBURSE_ROLES.includes(user?.role);
  const refData = usePaymentDeskRefData();
  const base = mode === "Cash" ? "/payment-desk/cash" : "/payment-desk/upi";
  const modeKey = mode.toLowerCase();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vendorFilter, setVendorFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [formTarget, setFormTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [reimburseTarget, setReimburseTarget] = useState(null);
  const [reimburseRef, setReimburseRef] = useState("");
  const [busy, setBusy] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const load = (pageNum = 1) => {
    setLoading(true);
    const params = { page: pageNum, page_size: 50 };
    if (vendorFilter !== "all") params.vendor_code = vendorFilter;
    if (mode === "Cash" && statusFilter !== "all") params.status = statusFilter;
    client
      .get(base, { params })
      .then((res) => {
        setEntries(res.data.items);
        setPage(res.data.page);
        setTotalPages(res.data.total_pages);
      })
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(1); }, [vendorFilter, statusFilter, mode]);

  const submitReimburse = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await client.post(`/payment-desk/cash/${reimburseTarget.entry_id}/reimburse`, { reference: reimburseRef.trim() || null });
      toast.success("Marked Reimbursed");
      setReimburseTarget(null);
      setReimburseRef("");
      load(page);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid={`${modeKey}-entries-tab`}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <SearchableSelect testId={`${modeKey}-vendor-filter`} className="w-48" value={vendorFilter} onValueChange={setVendorFilter} placeholder="All Vendors"
  options={[{ value: "all", label: "All Vendors", pinned: true }, ...refData.vendors.map((v) => ({ value: v.vendor_code, label: v.vendor_name, searchText: v.vendor_code }))]} />
          {mode === "Cash" && (
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="cash-status-filter" className="w-40">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="Logged">Logged</SelectItem>
                <SelectItem value="Reimbursed">Reimbursed</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>
        {canCreate && (
          <Button data-testid={`${modeKey}-add-button`} onClick={() => setFormTarget("new")} className="bg-amber-600 hover:bg-amber-700">
            <Plus className="h-4 w-4 mr-1.5" /> Log {mode} Entry
          </Button>
        )}
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <Table data-testid={`${modeKey}-table`}>
          <TableHeader>
            <TableRow>
              <TableHead>Entry ID</TableHead>
              <TableHead>Vendor</TableHead>
              <TableHead>Paid To</TableHead>
              <TableHead>Bill</TableHead>
              <TableHead>Attributed To</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Amount</TableHead>
              {mode === "Cash" && <TableHead>Status</TableHead>}
              <TableHead>Date</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={mode === "Cash" ? 9 : 8} className="text-center py-8 text-slate-400">Loading...</TableCell></TableRow>
            ) : entries.length === 0 ? (
              <TableRow><TableCell colSpan={mode === "Cash" ? 9 : 8} className="text-center py-8 text-slate-400">No {mode} entries found.</TableCell></TableRow>
            ) : (
              entries.map((en) => (
                <TableRow key={en.entry_id} data-testid={`${modeKey}-row-${en.entry_id}`}>
                  <TableCell className="font-mono text-xs font-semibold cursor-pointer" onClick={() => setFormTarget(en)}>{en.entry_id}</TableCell>
                  <TableCell className="text-xs">{en.vendor_name || "—"}</TableCell>
                  <TableCell><PayeeCell line={{ ...en, id: en.entry_id }} /></TableCell>
                  <TableCell className="font-mono text-xs">
                    {en.vendor_bill_id || "—"}
                    {en.bill_amount_applied != null && <span className="block text-slate-400">applied {money(en.bill_amount_applied)}</span>}
                  </TableCell>
                  <TableCell className="text-xs">{en.site_label || en.equipment_label}</TableCell>
                  <TableCell className="text-xs">{en.category_name}</TableCell>
                  <TableCell>{money(en.amount)}</TableCell>
                  {mode === "Cash" && <TableCell><Badge className={STATUS_BADGE[en.status]}>{en.status}</Badge></TableCell>}
                  <TableCell className="text-xs text-slate-500">{new Date(en.payment_date).toLocaleDateString("en-IN")}</TableCell>
                  <TableCell className="text-right space-x-1.5">
                    {en.can_correct ? (
                      <Button size="sm" variant="outline" data-testid={`${modeKey}-edit-${en.entry_id}`} onClick={() => setFormTarget(en)}>
                        Edit
                      </Button>
                    ) : (
                      <span className="text-[10px] text-slate-400" data-testid={`${modeKey}-window-closed-${en.entry_id}`}>Edit window closed</span>
                    )}
                    {en.can_delete && (
                      <Button size="sm" variant="destructive" disabled={busy} data-testid={`${modeKey}-delete-${en.entry_id}`} onClick={() => setDeleteTarget(en)}>
                        Delete
                      </Button>
                    )}
                    {en.can_reimburse && canReimburseRole && (
                      <Button size="sm" disabled={busy} data-testid={`cash-reimburse-${en.entry_id}`} onClick={() => setReimburseTarget(en)} className="bg-amber-600 hover:bg-amber-700">
                        Mark Reimbursed
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      <PaginationBar page={page} totalPages={totalPages} onPageChange={load} testId={`${modeKey}-pagination`} />

      {formTarget && (
        <PostFactoEntryFormDialog
          mode={mode}
          entry={formTarget === "new" ? null : formTarget}
          refData={refData}
          readOnly={formTarget !== "new" && !formTarget.can_correct}
          correctionMode={formTarget !== "new" && !!formTarget.can_correct}
          onClose={() => setFormTarget(null)}
          onSaved={() => {
            setFormTarget(null);
            load(page);
          }}
        />
      )}

      {deleteTarget && (
        <DeleteEntryDialog
          title={`Delete ${deleteTarget.entry_id}?`}
          endpoint={`/payment-desk/entries/${deleteTarget.entry_id}/delete`}
          details={[
            ["Entry", `${mode} ${deleteTarget.entry_id}`],
            ["Paid To", `${deleteTarget.payee_type}${deleteTarget.payee_name ? ` — ${deleteTarget.payee_name}` : ""}`],
            ["Amount", money(deleteTarget.amount)],
            ["Status", deleteTarget.status],
          ]}
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => { setDeleteTarget(null); load(page); }}
        />
      )}

      {reimburseTarget && (
        <Dialog open onOpenChange={(open) => !open && setReimburseTarget(null)}>
          <DialogContent className="max-w-md" data-testid="cash-reimburse-dialog">
            <DialogHeader>
              <DialogTitle>Mark {reimburseTarget.entry_id} Reimbursed</DialogTitle>
            </DialogHeader>
            <form onSubmit={submitReimburse} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Reimbursement Reference (optional)</label>
                <Input data-testid="cash-reimburse-reference-input" value={reimburseRef} onChange={(e) => setReimburseRef(e.target.value)} />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setReimburseTarget(null)} data-testid="cash-reimburse-cancel-button">Cancel</Button>
                <Button type="submit" disabled={busy} data-testid="cash-reimburse-submit-button" className="bg-amber-600 hover:bg-amber-700">
                  {busy ? "Saving..." : "Mark Reimbursed"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
